import { useGlobalAction } from "@gadgetinc/react";
import { BuildingOfficeIcon } from "@heroicons/react/16/solid";
import React, { useContext, useEffect, useState } from "react";
import { api } from "../api";
import { CustomerContext } from "../providers";
import LoadingSpinner from "./LoadingSpinner";
import { Dialog, DialogBody, DialogTitle } from "./tailwind/dialog";

export function SwitchCustomerDialog({ isOpen, onClose }) {
  const { customer } = useContext(CustomerContext);
  const [userCustomers, setUserCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [error, setError] = useState(null);
  const [, getUserCustomer] = useGlobalAction(api.getUserCustomer);
  const [, switchCustomer] = useGlobalAction(api.switchCustomer);

  useEffect(() => {
    if (isOpen) {
      setIsLoadingCustomers(true);
      setError(null);
      const fetchUserCustomers = async () => {
        try {
          const result = await getUserCustomer();
          if (result.data) {
            setUserCustomers(result.data);
          }
        } catch (error) {
          console.error("Error fetching user customers:", error);
        } finally {
          setIsLoadingCustomers(false);
        }
      };

      fetchUserCustomers();
    }
  }, [isOpen, getUserCustomer]);

  const handleSwitchCustomer = async (selectedCustomer) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await switchCustomer({ customerId: selectedCustomer.id });
      if (result.data?.success) {
        window.location.href = "/dashboard";
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (error) {
      console.error("Error switching company:", error);
      if (error.statusCode === 403) {
        setError("Access denied: You don't have access to this company");
      } else {
        setError(
          "An error occurred while switching companies. Please try again.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const availableCustomers = (userCustomers || []).filter(
    (userCustomer) => userCustomer.id !== customer.id,
  );

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle className="flex items-center gap-2">
        <BuildingOfficeIcon className="h-5 w-5" />
        Switch Company
      </DialogTitle>

      <DialogBody>
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="font-medium text-amber-900 mb-2">Current company</h3>
            <p className="text-amber-800">
              You are currently connected to <strong>{customer.name}</strong>
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {isLoadingCustomers || isLoading ? (
            <div className="text-center h-[100px] py-8">
              <LoadingSpinner />
              <p className="text-gray-600 mt-4">
                {isLoadingCustomers && "Loading companies..."}
                {isLoading && "Switching company..."}
              </p>
            </div>
          ) : availableCustomers.length > 0 ? (
            <div>
              <h3 className="font-medium text-gray-900 mb-3">
                Select a Company
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                You will be redirected to the workspace of the selected company.
              </p>

              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {availableCustomers.map((userCustomer, index) => (
                  <div
                    key={`${userCustomer.id}-${index}`}
                    className="p-3 border border-gray-200 rounded-lg cursor-pointer transition-colors hover:border-amber-300 hover:bg-amber-50"
                    onClick={() => handleSwitchCustomer(userCustomer)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-500" />
                        <div>
                          <p className="font-medium text-gray-900">
                            {userCustomer.name}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="font-medium text-gray-900 mb-2">
                No Other Companies Available
              </h3>
            </div>
          )}
        </div>
      </DialogBody>
    </Dialog>
  );
}
